import {
	Asset,
	AssetGrid,
	FilterCard,
	FilterProvider,
	GridMenuItems,
	InspectionScoredAttributes,
	PropertyPane,
	TabTypes,
	getMapLayerId,
	useFilter,
} from '@innovyze/map-components';
import {
	AssetDetails,
	InspDetails,
	TaskDetails,
} from '@innovyze/lib_am_common/Components';
import {
	Background,
	EditorModes,
	MapPosition,
	MapSelectedAssets,
	ReactMap,
	ReactMapProps,
} from '@innovyze/inno-map';
import { MapStatusType, RiskLayersMask } from '@innovyze/lib_am_common/Types';
import React, { useCallback, useMemo, useState } from 'react';
import StyledMap, { Popout, Popout2 } from './Map.styles';
import {
	configLayers,
	dataServicesMainMap,
	defaultInspectionSources,
	generalOptionsDefault,
	otherOptions,
	tileOptions,
} from '@innovyze/lib_am_common/Utils';
import {
	getMapLayers,
	getMapRiskLayers,
} from '@innovyze/lib_am_common/Actions';
import {
	getSubscriptions,
	isAdmin,
	useAuth0,
	useIsFeatureEnabled,
} from '@innovyze/stylovyze';
import { useReactMapConfig } from '@innovyze/stylovyze/lib/hooks/map/useReactMapConfig';
import {
	selectMapRehabLayers,
	selectRiskLayers,
} from '@innovyze/lib_am_common/Selectors';
import { useDispatch, useSelector } from 'react-redux';

import { AssetPopup } from '@Components/AssetPopup';
import { InspectionStandard } from '@Types';
import { assetsBaseUrl } from '@Apis/assets.api';
import { fullAccess } from '@innovyze/shared-utils';
import { geojsonRestictions } from '@Utils/map.utils';
import { haversine } from '@innovyze/shared-utils/lib/geometry/geometry';
import { inspectionStandard } from '@Utils/inspections.utills';
import { useDatagridFilter } from '@Hooks/useDatagridFilter';
import { useGlobalization } from '@Translations/useGlobalization';
import { useMapState } from '@Services/MapState';
import { usePolygonTool } from '@Hooks/usePolygonTool';

interface InspectionDetail {
	videoUrl: string;
	inspectionId: string;
	standard: InspectionStandard;
}

interface TaskDetail {
	taskId: string;
	taskType: string;
}

interface EventDetail {
	eventId: string;
	taskType: string;
}

interface AssetDetail {
	assetId: string;
	assetType: string;
	layerId?: string;
}

const logLevel = MapStatusType.WARN;

const useIsFeatureEnabledString = useIsFeatureEnabled as unknown as (
	feature: string,
) => boolean;

// eslint-disable-next-line @typescript-eslint/naming-convention
const MapPage = () => {
	const { t } = useGlobalization();
	const dispatch = useDispatch();
	const { token } = useAuth0();
	const iAmAdmin = isAdmin();
	const assetTrace = useIsFeatureEnabledString('info-360-map-asset-trace');
	const dynamicProps = useIsFeatureEnabledString('info-360-dynamic-props');
	const cbaRangedValues = useIsFeatureEnabledString(
		'map-color-by-attribute-ranged-values',
	);
	const assetGrid = useIsFeatureEnabledString('map-asset-grid');
	const assetGridAssetFocus = useIsFeatureEnabledString(
		'i-360-release-139226-find-assets-on-map-via-data-grid',
	);
	const themeEditorMode = iAmAdmin
		? EditorModes.editAll
		: EditorModes.editOwn;

	const tiledInspections = useIsFeatureEnabledString(
		'info-360-map-tiled-inspections',
	);
	// See comments in package.md wrt eslint-disable-line below
	const showRehab = useIsFeatureEnabledString('info-360-map-rehab');

	const subscriptions = getSubscriptions();
	const hasFullAccess = fullAccess(subscriptions);

	const [radius, setRadius] = useState<number>(
		geojsonRestictions.radiusDefault,
	);
	const [bbox, setBBox] = useState<number[]>([]);

	const [detailInspection, setDetailInspection] =
		useState<InspectionDetail | null>(null);

	const [detailTask, setDetailTask] = useState<TaskDetail | null>(null);
	const [detailEvent, setDetailEvent] = useState<EventDetail | null>(null);
	const [detailAsset, setDetailAsset] = useState<AssetDetail | null>(null);

	const [focusedAssets, setFocusedAssets] = useState<Asset[]>([]);
	const { setPolygonFilter, isPolygonToolEnabled } = usePolygonTool();
	const {
		datagridFilter,
		datagridFilterEndpoints,
		dataGridFilterCount = 0,
	} = useDatagridFilter();

	const { additionalFilterProps } = useFilter();

	const {
		position,
		setPosition,
		background,
		setBackground,
		selected,
		setSelected,
	} = useMapState();

	React.useEffect(() => {
		if (showRehab) {
			dispatch(getMapLayers());
		} else {
			dispatch(getMapRiskLayers());
		}
	}, []);

	const onBoundsChange = (bounds: number[]) => {
		let rad = geojsonRestictions.radiusDefault;

		if (bounds.length === 4) {
			const lon1 = bounds[0] + 0.5 * (bounds[2] - bounds[0]);
			const lat1 = bounds[1] + 0.5 * (bounds[3] - bounds[1]);
			rad = haversine(lat1, lon1, bounds[3], bounds[2]);
			rad *= geojsonRestictions.boundMargin;
		}

		setRadius(Math.ceil(rad));
		setBBox(bounds);
	};

	const onPositionChange = (newPosition: MapPosition) => {
		// If the current position is undefined (e.g. a fresh browse to the map page),
		//   and the map is telling us that it is sitting at the default unspecified position (0,0 zoom 3)
		//   don't set the position (which actually tells the map to go to (and stay at) this position...)
		// This will allow data to set the boundary of the map automatically.
		if (
			position === undefined &&
			newPosition.center[0] === 0 &&
			newPosition.center[1] === 0 &&
			newPosition.zoom === 3
		) {
			return;
		}

		setPosition(newPosition);
	};

	const onBackgroundChanged = (newBackground: Background) => {
		setBackground(newBackground.key);
	};

	const onSelectedAssetsChange = (items: MapSelectedAssets | null) => {
		setSelected(items || []);
	};

	const mask = RiskLayersMask.Published;
	const riskLayers = selectRiskLayers(mask);
	const rehabLayers = useSelector(selectMapRehabLayers);

	const options = {
		showRisk: true,
		riskLayersMask: mask,
		riskLayers,
		generalOptions: { ...generalOptionsDefault },
		tilesSource: { ...tileOptions },
		inspectionSources: defaultInspectionSources,
		otherSource: { ...otherOptions },
		rehabLayers: showRehab ? rehabLayers : undefined,
	};

	// Would like to use version rather than index
	// but currently need to set propertyLookup to valid calls
	// on first dataServices load - as subsequent changes are
	// ignored in inno-map
	const riskGroupPublishLimit = 10;

	const dataServices = useMemo(
		() =>
			dataServicesMainMap(
				options,
				token,
				position,
				radius,
				bbox,
				riskGroupPublishLimit,
				tiledInspections,
			),
		[token, position, radius, bbox, options],
	);

	const layerConfigure = useMemo(
		() => configLayers(token, options, [], tiledInspections),
		[token, options, tiledInspections],
	);

	const resetDetailInspection = useCallback(() => {
		setDetailInspection(null);
	}, []);

	const resetDetailTask = useCallback(() => {
		setDetailTask(null);
	}, []);

	const resetDetailEvent = useCallback(() => {
		setDetailEvent(null);
	}, []);

	const resetDetailAsset = useCallback(() => {
		setDetailAsset(null);
	}, []);

	const onClickAway = useCallback(() => {
		resetDetailInspection();
		resetDetailAsset();
	}, []);

	const onInspDetailClicked = useCallback(
		(
			videoUrl: string,
			inspectionId: string,
			standard: InspectionStandard,
		) => {
			setDetailInspection({ videoUrl, inspectionId, standard });
		},
		[],
	);

	const onTaskDetailClicked = useCallback(
		(taskId: string, standard: InspectionStandard) => {
			setDetailTask({ taskId, taskType: standard });
		},
		[],
	);

	const onEventDetailClicked = useCallback(
		(eventId: string, standard: InspectionStandard) => {
			setDetailEvent({ eventId, taskType: standard });
		},
		[],
	);

	const inspDetailActions = {
		onInspectionDetailClicked: onInspDetailClicked,
		onTaskDetailClicked: onTaskDetailClicked,
		onEventDetailClicked: onEventDetailClicked,
	};

	const onViewInspection = useCallback(
		(inspection: InspectionScoredAttributes) => {
			const videoUrl = inspection.videoUrl;
			const inspectionId = inspection._id;
			const standard = inspectionStandard(inspection.inspectionVersion);
			setDetailInspection({ videoUrl, inspectionId, standard });
		},
		[],
	);

	const onViewTask = useCallback((inspection: InspectionScoredAttributes) => {
		const taskId = inspection._id;
		const taskType = inspection.inspectionVersion;
		setDetailTask({ taskId, taskType });
	}, []);

	const onViewEvent = useCallback(
		(inspection: InspectionScoredAttributes) => {
			const eventId = inspection._id;
			const taskType = inspection.inspectionVersion;
			setDetailEvent({ eventId, taskType });
		},
		[],
	);

	const onViewAssetDetail = useCallback(
		(
			id: string,
			layerId?: string,
			assetId?: string,
			assetType?: string,
		) => {
			if (assetId && assetType) {
				setDetailAsset({ assetId, assetType, layerId });
			} else {
				const [assetTypeFromId, assetIdFromId] = id.split('#');
				setDetailAsset({
					assetId: assetIdFromId,
					assetType: assetTypeFromId,
					layerId,
				});
			}
		},
		[],
	);

	const searchEndpoints = useMemo(
		() => [
			{
				url: `${assetsBaseUrl}/assets?fuzzySearch={{query}}`,
				token: token,
				dataServiceId: 'tiles',
			},
		],
		[token],
	);

	const tracingEndpoint = useMemo(
		() =>
			assetTrace
				? {
						url: `${assetsBaseUrl}/assets/{{assetId}}/trace?assetType={{assetType}}`,
						token,
				  }
				: undefined,
		[token],
	);

	// Only showing if a selected item type supports risk inspections
	const showInspections =
		selected.find(item =>
			['wwPipe', 'wwManhole', 'pipe'].some(x => x === item.assetType),
		) !== undefined;

	const gridActionItems = [];
	const excludeFilter = useIsFeatureEnabled(
		'i-360-release-145677-remove-asset-from-filter',
	);
	if (excludeFilter) {
		gridActionItems.push(GridMenuItems.exclude);
	}

	const reactMapConfig = useReactMapConfig();

	return (
		<StyledMap>
			{detailInspection && (
				<Popout2>
					<InspDetails
						// videoUrl={detailInspection.videoUrl}
						back={{
							action: resetDetailInspection,
							label: t('Back to Asset Details'),
						}}
						inspectionId={detailInspection.inspectionId}
						standard={detailInspection.standard}
					/>
				</Popout2>
			)}
			{detailTask && (
				<Popout2>
					<TaskDetails
						back={{
							action: resetDetailTask,
							label: t('Back to Asset Details'),
						}}
						taskType={detailTask.taskType}
						taskId={detailTask.taskId}
						type={'task'}
					/>
				</Popout2>
			)}
			{detailEvent && (
				<Popout2>
					<TaskDetails
						back={{
							action: resetDetailEvent,
							label: t('Back to Asset Details'),
						}}
						taskType={detailEvent.taskType}
						taskId={detailEvent.eventId}
						type={'event'}
					/>
				</Popout2>
			)}
			{detailAsset && (
				<Popout>
					<AssetDetails
						assetId={detailAsset.assetId}
						back={{ action: onClickAway, label: t('Back to map') }}
						assetType={detailAsset.assetType}
						actions={inspDetailActions}
						adminFullAccess={hasFullAccess}
						layerId={detailAsset.layerId}
					/>
				</Popout>
			)}
			<ReactMap
				config={reactMapConfig}
				dataServices={dataServices}
				layerConfig={layerConfigure}
				summaryPanel={
					<PropertyPane
						tabsToDisplay={[TabTypes.asset, TabTypes.spatial]}
						viewInspection={onViewInspection}
						viewAssetDetails={onViewAssetDetail}
						viewTask={onViewTask}
						viewEvent={onViewEvent}
						viewDetailsForAssetTypes={
							dynamicProps
								? undefined
								: ['wwPipe', 'wwManhole', 'pipe']
						}
						showInspections={showInspections}
					/>
				}
				position={position}
				background={background}
				onPositionChange={onPositionChange}
				onBoundsChange={onBoundsChange}
				onBackgroundChanged={onBackgroundChanged}
				selectedAssets={selected}
				onSelectedAssetsChange={onSelectedAssetsChange}
				searchEndpoints={searchEndpoints}
				tracingEndpoint={tracingEndpoint}
				themeEditorMode={themeEditorMode}
				hoverPopup={<AssetPopup />}
				logLevel={logLevel ?? MapStatusType.WARN}
				enableColorByAttributeRange={cbaRangedValues}
				datagridFilter={datagridFilter}
				datagridFilterEndpoints={datagridFilterEndpoints}
				focusedAssets={assetGridAssetFocus ? focusedAssets : undefined}
				zoomToFocusedAssets={assetGridAssetFocus}
				polygonControl={
					isPolygonToolEnabled && !!additionalFilterProps.assetType
				}
				onPolygonComplete={
					setPolygonFilter as ReactMapProps['onPolygonComplete']
				}
				assetGrid={
					assetGrid ? (
						<AssetGrid
							onViewDetails={({
								assetId,
								assetType,
								systemType,
							}) => {
								// Generate the layerId from the systemType
								const layerId = getMapLayerId(
									assetType,
									systemType,
								);
								setDetailAsset({ assetId, assetType, layerId });
							}}
							onSelectedAssetsChange={
								assetGridAssetFocus
									? setFocusedAssets
									: undefined
							}
							actionMenuItems={gridActionItems}
						/>
					) : undefined
				}
			/>
			{dataGridFilterCount > 0 && (
				<FilterCard filterCount={dataGridFilterCount} />
			)}
		</StyledMap>
	);
};

export const Map = () => {
	return (
		<FilterProvider>
			<MapPage />
		</FilterProvider>
	);
};

export default Map;
